import React, { useCallback, useContext } from 'react'

import CommunityGalleryPanel from 'Pods/LazyLoaded/CommunityGalleryPanel/CommunityGalleryPanel'
import { ANONYMOUS_PAGE_LIMIT } from 'Pods/LazyLoaded/CommunityGalleryPanel/constants'
import { useNavigate } from 'Pods/Shared/ContextBasedRouter'
import { buildSharedRunPathname } from 'Pods/ThreeDStudio/Hooks/buildSharedRunPathname'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  clearAllFilters, getFiltersCount, setCategory, setSearchQuery,
} from 'Pods/ThreeDStudio/Store/actions/communityGalleryFilters'
import { openSignInModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import EmptyState from './Components/EmptyState/EmptyState'

// ThreeDStudio wiring for the shared CommunityGalleryPanel: maps the Zustand
// filter store + SSR page context to the panel's controlled props, and supplies
// the open-in-viewer card action (gallery click sends the user into Remix Mode)
// that only makes sense on this page. Other hosts (e.g. the AI landing page)
// provide their own wiring.
function CommunityGalleryPanelContainer() {
  const {
    communityGalleryFilterOptions,
    communityGalleryInitialModels,
    userLoggedIn,
    pageConfigLoaded,
  } = useContext(ThreeDStudioPageContext)
  const searchQuery = useThreeDStudioStore(state => state.communityGalleryFilters.searchQuery)
  const category = useThreeDStudioStore(state => state.communityGalleryFilters.category)
  const sortOrder = useThreeDStudioStore(state => state.communityGalleryFilters.sortOrder)
  const hasActiveFilters = useThreeDStudioStore(state => getFiltersCount(state) > 0)

  const navigate = useNavigate()

  const handleCardClick = useCallback((model) => {
    // Opening a model from the gallery puts the user in Remix Mode. Origin travels on
    // the navigation itself (`state: { fromGallery }`) so the viewer can show the
    // one-time "You are in Remix Mode" notice and flip the CTA to "Regenerate 3D Model";
    // any other way of opening a model (library card, deep link, refresh) carries no such
    // state, keeping it gallery-only. The model opens read-only: useModelUrlSync fetches
    // it from the public slug endpoint after navigation, and nothing is added to the
    // library until the user actually generates.
    navigate(
      buildSharedRunPathname({ slug: model.slug, id: model.id, title: model.title }),
      { state: { fromGallery: true } },
    )
  }, [navigate])

  // Anonymous visitors can scroll through a capped number of gallery pages;
  // reaching the bottom prompts a sign up / log in instead of loading more.
  // Only gate once the per-user config has loaded and confirmed the visitor is
  // anonymous, so a signed-in user is never briefly prompted while it resolves.
  const anonymousPageLimit = pageConfigLoaded && !userLoggedIn ? ANONYMOUS_PAGE_LIMIT : null

  const handleAuthGatedLoadMore = useCallback(() => {
    openSignInModal('signup')
  }, [])

  return (
    <CommunityGalleryPanel
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      category={category}
      categories={communityGalleryFilterOptions.visibleCategories}
      onCategoryChange={setCategory}
      sortOrder={sortOrder}
      initialModels={communityGalleryInitialModels}
      paginationMode="infinite"
      onCardClick={handleCardClick}
      onAuthGatedLoadMore={handleAuthGatedLoadMore}
      anonymousPageLimit={anonymousPageLimit}
      emptyState={(
        <EmptyState hasActiveFilters={hasActiveFilters} onClearFilters={clearAllFilters} />
      )}
    />
  )
}

export default CommunityGalleryPanelContainer
